import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API_BASE } from "@/App";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem("ranker-auth") : null;
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored);
      return parsed.user || null;
    } catch {
      return null;
    }
  });

  const [token, setToken] = useState(() => {
    const stored = typeof window !== "undefined" ? localStorage.getItem("ranker-auth") : null;
    if (!stored) return null;
    try {
      const parsed = JSON.parse(stored);
      const storedToken = parsed.token || null;
      // Set axios header immediately on initialization
      if (storedToken) {
        axios.defaults.headers.common.Authorization = `Bearer ${storedToken}`;
      }
      return storedToken;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  // Refresh user data from server to get latest role and purchased_optionals
  const refreshUser = useCallback(async () => {
    if (!token) return;
    
    try {
      const { data } = await axios.get(`${API_BASE}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (data && data.id) {
        // Update user in state and localStorage
        setUser(data);
        const stored = localStorage.getItem("ranker-auth");
        if (stored) {
          const parsed = JSON.parse(stored);
          parsed.user = data;
          localStorage.setItem("ranker-auth", JSON.stringify(parsed));
        }
        console.log("✅ User data refreshed:", data.role);
      }
    } catch (error) {
      // If 401, token is invalid - logout
      if (error.response?.status === 401) {
        console.log("Token invalid, logging out");
        setUser(null);
        setToken(null);
        localStorage.removeItem("ranker-auth");
        delete axios.defaults.headers.common.Authorization;
      }
    }
  }, [token]);

  // Refresh user data on app load if logged in
  useEffect(() => {
    if (token && user) {
      // Refresh user data to get latest role
      refreshUser();
    }
  }, []); // Only on mount

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete axios.defaults.headers.common.Authorization;
    }
  }, [token]);

  const saveAuth = (payload) => {
    const tokenValue = payload.token.access_token || payload.token;
    setUser(payload.user);
    setToken(tokenValue);
    axios.defaults.headers.common.Authorization = `Bearer ${tokenValue}`;
    localStorage.setItem(
      "ranker-auth",
      JSON.stringify({ user: payload.user, token: tokenValue }),
    );
    // Attribute referral if a code was captured from ?ref= at signup/login.
    try {
      const ref = localStorage.getItem("clarias_ref");
      if (ref) {
        axios.post(`${API_BASE}/referrals/attribute`, { code: ref }, {
          headers: { Authorization: `Bearer ${tokenValue}` },
        }).catch(() => {}).finally(() => localStorage.removeItem("clarias_ref"));
      }
    } catch (e) { /* noop */ }
  };

  const login = async (email, password) => {
    try {
      const form = new URLSearchParams();
      form.append("username", email);
      form.append("password", password);

      const { data } = await axios.post(`${API_BASE}/auth/login`, form, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
      });

      console.log("Login response:", data);
      saveAuth(data);
      
      // Return a promise that resolves after state update
      return new Promise((resolve) => {
        setTimeout(() => resolve(data.user), 150);
      });
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  const register = async (name, email, password) => {
    const { data } = await axios.post(`${API_BASE}/auth/register`, {
      name,
      email,
      password,
    });
    saveAuth(data);
    
    // Return a promise that resolves after state update
    return new Promise((resolve) => {
      setTimeout(() => resolve(data.user), 100);
    });
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    delete axios.defaults.headers.common.Authorization;
    localStorage.removeItem("ranker-auth");
  };

  const setAuth = (payload) => {
    saveAuth({ user: payload.user, token: { access_token: payload.token } });
  };

  const value = { user, token, loading, login, register, logout, setUser, setAuth, refreshUser };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
